// =============================================================================
// computedBadges.ts — automatic, data-derived badges (v0.2.31)
// =============================================================================
//
// Computed badges are small visual labels derived automatically from a
// scholarship's (or inbox item's) own data — amount, deadline, requirement
// fields, status. They recalculate every render, so they are always current:
// a "Due soon" badge appears on its own as a deadline approaches and a
// "Submitted" badge replaces the deadline badges once submitted.
//
// They are SEPARATE from the manual `tags` field. Tags are Mia's own labels;
// badges are the app stating facts. Both are shown.
//
// Design rule: only show a badge when its condition is DEFINITELY true. Fields
// that are null/unknown produce no badge — never a guessed one. A scholarship
// with unknown essay requirements simply gets no "No essay" badge.
// =============================================================================

import type { Scholarship, InboxItem, Profile } from '@scholarship-app/types';

export type BadgeId =
  | 'high_value'
  | 'due_soon'
  | 'overdue'
  | 'local'
  | 'no_essay'
  | 'need_based'
  | 'homeschool_friendly'
  | 'submitted';

export interface ComputedBadge {
  id: BadgeId;
  label: string;
  /** Tabler-style icon name (lucide-react component chosen in the component). */
  icon: BadgeId;
}

// Thresholds — confirmed with the user.
const HIGH_VALUE_MIN = 5000;
const DUE_SOON_DAYS = 14;

const BADGE_LABELS: Record<BadgeId, string> = {
  high_value: 'High value',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  local: 'Local',
  no_essay: 'No essay',
  need_based: 'Need-based',
  homeschool_friendly: 'Homeschool-friendly',
  submitted: 'Submitted',
};

function badge(id: BadgeId): ComputedBadge {
  return { id, label: BADGE_LABELS[id], icon: id };
}

/** Days from now until an ISO date string. Negative = in the past. */
function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

/** The larger of amount_min / amount_max, or null if neither is set. */
function bestAmount(min: number | null, max: number | null): number | null {
  const vals = [min, max].filter((v): v is number => typeof v === 'number');
  return vals.length ? Math.max(...vals) : null;
}

/**
 * Does this scholarship look tied to the applicant's state/region?
 * Best-effort: checks demographic_restrictions text against the profile's
 * state. Only returns true on a clear match — never guesses.
 */
function looksLocal(
  restrictions: string[] | undefined,
  profile: Profile | null
): boolean {
  if (!profile || !restrictions || restrictions.length === 0) return false;
  const state = (profile.address?.state ?? '').trim().toLowerCase();
  if (!state) return false;
  return restrictions.some((r) => r.toLowerCase().includes(state));
}

/**
 * Compute badges for a tracked scholarship. `profile` is optional and only
 * used for the best-effort "Local" badge.
 */
export function computeScholarshipBadges(
  s: Scholarship,
  profile: Profile | null = null
): ComputedBadge[] {
  const out: ComputedBadge[] = [];

  const isSubmitted =
    s.status === 'submitted' || s.status === 'awarded' || s.status === 'rejected';

  // Submitted — shown instead of the deadline badges once done.
  if (isSubmitted) out.push(badge('submitted'));

  // Amount.
  const amount = bestAmount(s.amount_min, s.amount_max);
  if (amount !== null && amount >= HIGH_VALUE_MIN) out.push(badge('high_value'));

  // Deadline — use effective_deadline, fall back to deadline. Not shown once
  // submitted (deadline pressure is moot).
  if (!isSubmitted) {
    const deadlineIso = s.effective_deadline ?? s.deadline;
    const d = daysUntil(deadlineIso);
    if (d !== null) {
      if (d < 0) out.push(badge('overdue'));
      else if (d <= DUE_SOON_DAYS) out.push(badge('due_soon'));
    }
  }

  // Requirement-derived (only when definitely true).
  if (s.requirements && s.requirements.essay_required === false) {
    out.push(badge('no_essay'));
  }
  if (s.need_based === true) out.push(badge('need_based'));
  if (s.homeschool_friendly === true) out.push(badge('homeschool_friendly'));

  // Local — best-effort.
  if (looksLocal(s.demographic_restrictions, profile)) out.push(badge('local'));

  return out;
}

/**
 * Compute badges for an inbox item. Inbox items carry fewer fields than
 * tracked scholarships — no `requirements` and no submitted/deadline status —
 * so only the badges their data supports are produced: High value, Due soon,
 * Need-based, Homeschool-friendly.
 */
export function computeInboxBadges(
  item: InboxItem,
  profile: Profile | null = null
): ComputedBadge[] {
  const out: ComputedBadge[] = [];

  const amount = bestAmount(item.amount_min, item.amount_max);
  if (amount !== null && amount >= HIGH_VALUE_MIN) out.push(badge('high_value'));

  const d = daysUntil(item.deadline_approx);
  if (d !== null && d >= 0 && d <= DUE_SOON_DAYS) out.push(badge('due_soon'));

  if (item.need_based === true) out.push(badge('need_based'));
  if (item.homeschool_friendly === true) out.push(badge('homeschool_friendly'));

  // Local — best-effort. Inbox items have no demographic_restrictions array,
  // so this is omitted for inbox.
  void profile;

  return out;
}
